"use client";

import React, { useState, useMemo } from "react";

// ====================================================
// A) INPUT TYPES & INTERFACES
// ====================================================

type ScenarioType = "CONSERVATIVE" | "EXPECTED" | "AGGRESSIVE";

interface CalculatorInputs {
  // A1. Current State
  MONTHLY_REVENUE: number;
  AVG_DEAL_SIZE: number;
  SALES_CYCLE_DAYS: number;

  // A2. Funnel Volumes & Rates
  QUALIFIED_LEADS_KNOWN: boolean;
  MONTHLY_QUALIFIED_LEADS: number;
  LEAD_TO_MEETING_RATE: number;
  MEETING_TO_CLOSE_RATE: number;

  // A3. Team & Capacity
  NUM_SDR: number;
  NUM_AE: number;
  SDR_MAX_MEETINGS_PER_MONTH: number;
  AE_MAX_MEETINGS_PER_MONTH: number;

  // A4. Founder-as-Seller
  FOUNDER_IS_SELLER: boolean;
  FOUNDER_MAX_MEETINGS_PER_MONTH: number;

  // A5. AI Outbound Engine
  AI_OUTBOUND_ENABLED: boolean;
  AI_OUTBOUND_LEADS_PER_MONTH: number;
  AI_OUTBOUND_TO_MEETING_RATE: number;

  // A6. AI Cost
  AI_MONTHLY_COST: number;

  // A7. Scenario
  SCENARIO: ScenarioType;

  // A8. Detractors
  D_TOO_MANY_UNQUALIFIED_LEADS: boolean;
  D_NOT_ENOUGH_LEADS: boolean;
  D_SLOW_RESPONSE_TIME: boolean;
  D_INCONSISTENT_FOLLOWUP: boolean;
  D_POOR_LEAD_ROUTING: boolean;
  D_MISS_DISCOVERY_QUESTIONS: boolean;
  D_INCONSISTENT_QUALIFICATION: boolean;
  D_DEALS_STALL_POST_DEMO: boolean;
  D_WEAK_PROPOSALS_NEXT_STEPS: boolean;
  D_FOUNDER_REQUIRED_FOR_DEALS: boolean;
  D_TRIBAL_KNOWLEDGE_LOCKED: boolean;
  D_LOW_TECHNICAL_DEPTH: boolean;
  D_TOOLS_NOT_INTEGRATED: boolean;
  D_MANUAL_CRM_UPDATES: boolean;
  D_LOW_PIPELINE_VISIBILITY: boolean;
}

interface CalculationResults {
  // Scenario
  SCENARIO_MULT: number;

  // Detractor Lifts
  CLOSE_LIFT_RAW: number;
  L2M_LIFT_RAW: number;
  CYCLE_REDUCTION_RAW: number;
  SELLER_CAPACITY_LIFT_RAW: number;
  CLOSE_LIFT: number;
  L2M_LIFT: number;
  CYCLE_REDUCTION: number;
  SELLER_CAPACITY_LIFT: number;

  // Capacities
  FOUNDERS_MEETING_CAPACITY: number;
  SELLER_MEETING_CAPACITY: number;
  SETTER_MEETING_CAPACITY: number;

  // Baseline Funnel
  BASE_DEALS_FROM_REVENUE: number;
  BASE_MEETINGS_REQUIRED: number;
  EST_MONTHLY_QUALIFIED_LEADS: number;
  BASE_QUALIFIED_LEADS_USED: number;
  BASE_MEETINGS_DEMAND: number;
  BASE_MEETINGS_REALIZED: number;
  BASE_DEALS_CLOSED: number;
  BASE_REVENUE_MODELED: number;
  BASE_REVENUE: number;

  // AI Outbound
  AI_OUTBOUND_BOOKED_MEETINGS: number;

  // Post-AI Funnel
  AI_LEAD_TO_MEETING_RATE: number;
  AI_MEETING_TO_CLOSE_RATE: number;
  AI_SALES_CYCLE_DAYS: number;
  AI_MEETINGS_DEMAND_FROM_BASE_LEADS: number;
  AI_MEETINGS_DEMAND_TOTAL: number;
  AI_SELLER_MEETING_CAPACITY: number;
  AI_MEETINGS_REALIZED: number;
  AI_DEALS_CLOSED: number;
  AI_REVENUE_MODELED: number;
  MODELED_UPLIFT: number;
  AI_REVENUE: number;
  MONTHLY_REVENUE_LIFT: number;

  // ROI Outputs
  ANNUAL_REVENUE_LIFT: number;
  ANNUAL_AI_COST: number;
  NET_MONTHLY_BENEFIT: number;
  NET_ANNUAL_BENEFIT: number;
  PAYBACK_MONTHS: number | string;
  ROI_12_MONTHS: number | string;
}

// ====================================================
// B) CONSTANTS & HELPERS
// ====================================================

const REVENUE_BLEND = 0.7;

function CLAMP(x: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, x));
}

function getScenarioMultiplier(scenario: ScenarioType): number {
  switch (scenario) {
    case "CONSERVATIVE":
      return 0.6;
    case "EXPECTED":
      return 1.0;
    case "AGGRESSIVE":
      return 1.4;
    default:
      return 1.0;
  }
}

// ====================================================
// C) CALCULATION ENGINE
// ====================================================

function calculateResults(inputs: CalculatorInputs): CalculationResults {
  // B1. Scenario multiplier
  const SCENARIO_MULT = getScenarioMultiplier(inputs.SCENARIO);

  // C1. CLOSE RATE lift contributions
  const CLOSE_LIFT_RAW =
    (inputs.D_TOO_MANY_UNQUALIFIED_LEADS ? 0.02 : 0) +
    (inputs.D_NOT_ENOUGH_LEADS ? 0.03 : 0) +
    (inputs.D_SLOW_RESPONSE_TIME ? 0.01 : 0) +
    (inputs.D_INCONSISTENT_FOLLOWUP ? 0.02 : 0) +
    (inputs.D_POOR_LEAD_ROUTING ? 0.01 : 0) +
    (inputs.D_MISS_DISCOVERY_QUESTIONS ? 0.03 : 0) +
    (inputs.D_INCONSISTENT_QUALIFICATION ? 0.02 : 0) +
    (inputs.D_DEALS_STALL_POST_DEMO ? 0.02 : 0) +
    (inputs.D_WEAK_PROPOSALS_NEXT_STEPS ? 0.02 : 0) +
    (inputs.D_FOUNDER_REQUIRED_FOR_DEALS ? 0.03 : 0) +
    (inputs.D_TRIBAL_KNOWLEDGE_LOCKED ? 0.02 : 0) +
    (inputs.D_LOW_TECHNICAL_DEPTH ? 0.02 : 0) +
    (inputs.D_TOOLS_NOT_INTEGRATED ? 0.01 : 0) +
    (inputs.D_MANUAL_CRM_UPDATES ? 0.01 : 0) +
    (inputs.D_LOW_PIPELINE_VISIBILITY ? 0.01 : 0);

  // C2. LEAD→MEETING lift contributions
  const L2M_LIFT_RAW =
    (inputs.D_SLOW_RESPONSE_TIME ? 0.03 : 0) +
    (inputs.D_INCONSISTENT_FOLLOWUP ? 0.03 : 0) +
    (inputs.D_POOR_LEAD_ROUTING ? 0.02 : 0) +
    (inputs.D_TOOLS_NOT_INTEGRATED ? 0.01 : 0);

  // C3. Sales cycle reduction contributions
  const CYCLE_REDUCTION_RAW =
    (inputs.D_SLOW_RESPONSE_TIME ? 0.05 : 0) +
    (inputs.D_INCONSISTENT_FOLLOWUP ? 0.05 : 0) +
    (inputs.D_WEAK_PROPOSALS_NEXT_STEPS ? 0.04 : 0) +
    (inputs.D_MANUAL_CRM_UPDATES ? 0.03 : 0) +
    (inputs.D_TOOLS_NOT_INTEGRATED ? 0.03 : 0) +
    (inputs.D_LOW_PIPELINE_VISIBILITY ? 0.02 : 0);

  // C4. Seller capacity lift contributions
  const SELLER_CAPACITY_LIFT_RAW =
    (inputs.D_MANUAL_CRM_UPDATES ? 0.08 : 0) +
    (inputs.D_TOOLS_NOT_INTEGRATED ? 0.06 : 0) +
    (inputs.D_LOW_PIPELINE_VISIBILITY ? 0.04 : 0);

  // C5. Apply scenario multiplier and clamps
  const CLOSE_LIFT = CLAMP(CLOSE_LIFT_RAW * SCENARIO_MULT, 0, 0.2);
  const L2M_LIFT = CLAMP(L2M_LIFT_RAW * SCENARIO_MULT, 0, 0.2);
  const CYCLE_REDUCTION = CLAMP(CYCLE_REDUCTION_RAW * SCENARIO_MULT, 0, 0.4);
  const SELLER_CAPACITY_LIFT = CLAMP(
    SELLER_CAPACITY_LIFT_RAW * SCENARIO_MULT,
    0,
    0.3
  );

  // D) CAPACITY MODEL
  const FOUNDERS_MEETING_CAPACITY = inputs.FOUNDER_IS_SELLER
    ? inputs.FOUNDER_MAX_MEETINGS_PER_MONTH
    : 0;

  let SELLER_MEETING_CAPACITY =
    inputs.NUM_AE * inputs.AE_MAX_MEETINGS_PER_MONTH +
    FOUNDERS_MEETING_CAPACITY;

  // D3. Safety guard
  SELLER_MEETING_CAPACITY = Math.max(
    SELLER_MEETING_CAPACITY,
    inputs.FOUNDER_IS_SELLER ? 1 : 0
  );

  // D4. Setter meeting capacity
  const SETTER_MEETING_CAPACITY =
    inputs.NUM_SDR > 0
      ? inputs.NUM_SDR * inputs.SDR_MAX_MEETINGS_PER_MONTH
      : 999999999;

  // E) BASELINE FUNNEL
  const BASE_DEALS_FROM_REVENUE =
    inputs.AVG_DEAL_SIZE > 0 ? inputs.MONTHLY_REVENUE / inputs.AVG_DEAL_SIZE : 0;

  const BASE_MEETINGS_REQUIRED =
    inputs.MEETING_TO_CLOSE_RATE > 0
      ? BASE_DEALS_FROM_REVENUE / inputs.MEETING_TO_CLOSE_RATE
      : 0;

  const EST_MONTHLY_QUALIFIED_LEADS =
    inputs.LEAD_TO_MEETING_RATE > 0
      ? BASE_MEETINGS_REQUIRED / inputs.LEAD_TO_MEETING_RATE
      : 0;

  const BASE_QUALIFIED_LEADS_USED = inputs.QUALIFIED_LEADS_KNOWN
    ? inputs.MONTHLY_QUALIFIED_LEADS
    : EST_MONTHLY_QUALIFIED_LEADS;

  const BASE_MEETINGS_DEMAND =
    BASE_QUALIFIED_LEADS_USED * inputs.LEAD_TO_MEETING_RATE;

  const BASE_MEETINGS_REALIZED = Math.min(
    BASE_MEETINGS_DEMAND,
    SETTER_MEETING_CAPACITY,
    SELLER_MEETING_CAPACITY
  );

  const BASE_DEALS_CLOSED =
    BASE_MEETINGS_REALIZED * inputs.MEETING_TO_CLOSE_RATE;

  const BASE_REVENUE_MODELED = BASE_DEALS_CLOSED * inputs.AVG_DEAL_SIZE;

  const BASE_REVENUE =
    REVENUE_BLEND * inputs.MONTHLY_REVENUE +
    (1 - REVENUE_BLEND) * BASE_REVENUE_MODELED;

  // F) AI OUTBOUND MEETING GENERATION
  const AI_OUTBOUND_BOOKED_MEETINGS = inputs.AI_OUTBOUND_ENABLED
    ? inputs.AI_OUTBOUND_LEADS_PER_MONTH * inputs.AI_OUTBOUND_TO_MEETING_RATE
    : 0;

  // G) POST-AI FUNNEL
  const AI_LEAD_TO_MEETING_RATE = CLAMP(
    inputs.LEAD_TO_MEETING_RATE + L2M_LIFT,
    0,
    1
  );

  const AI_MEETING_TO_CLOSE_RATE = CLAMP(
    inputs.MEETING_TO_CLOSE_RATE + CLOSE_LIFT,
    0,
    1
  );

  const AI_SALES_CYCLE_DAYS = inputs.SALES_CYCLE_DAYS * (1 - CYCLE_REDUCTION);

  const AI_MEETINGS_DEMAND_FROM_BASE_LEADS =
    BASE_QUALIFIED_LEADS_USED * AI_LEAD_TO_MEETING_RATE;

  const AI_MEETINGS_DEMAND_TOTAL =
    AI_MEETINGS_DEMAND_FROM_BASE_LEADS + AI_OUTBOUND_BOOKED_MEETINGS;

  const AI_SELLER_MEETING_CAPACITY =
    SELLER_MEETING_CAPACITY * (1 + SELLER_CAPACITY_LIFT);

  const AI_MEETINGS_REALIZED = Math.min(
    AI_MEETINGS_DEMAND_TOTAL,
    SETTER_MEETING_CAPACITY,
    AI_SELLER_MEETING_CAPACITY
  );

  const AI_DEALS_CLOSED = AI_MEETINGS_REALIZED * AI_MEETING_TO_CLOSE_RATE;

  const AI_REVENUE_MODELED = AI_DEALS_CLOSED * inputs.AVG_DEAL_SIZE;

  const MODELED_UPLIFT = Math.max(0, AI_REVENUE_MODELED - BASE_REVENUE_MODELED);

  const AI_REVENUE = BASE_REVENUE + MODELED_UPLIFT;

  const MONTHLY_REVENUE_LIFT = AI_REVENUE - BASE_REVENUE;

  // H) REVENUE-ONLY ROI OUTPUTS
  const ANNUAL_REVENUE_LIFT = MONTHLY_REVENUE_LIFT * 12;
  const ANNUAL_AI_COST = inputs.AI_MONTHLY_COST * 12;
  const NET_MONTHLY_BENEFIT = MONTHLY_REVENUE_LIFT - inputs.AI_MONTHLY_COST;
  const NET_ANNUAL_BENEFIT = ANNUAL_REVENUE_LIFT - ANNUAL_AI_COST;

  const PAYBACK_MONTHS =
    MONTHLY_REVENUE_LIFT <= 0
      ? "No payback"
      : inputs.AI_MONTHLY_COST / MONTHLY_REVENUE_LIFT;

  const ROI_12_MONTHS =
    ANNUAL_AI_COST <= 0 ? "" : NET_ANNUAL_BENEFIT / ANNUAL_AI_COST;

  return {
    SCENARIO_MULT,
    CLOSE_LIFT_RAW,
    L2M_LIFT_RAW,
    CYCLE_REDUCTION_RAW,
    SELLER_CAPACITY_LIFT_RAW,
    CLOSE_LIFT,
    L2M_LIFT,
    CYCLE_REDUCTION,
    SELLER_CAPACITY_LIFT,
    FOUNDERS_MEETING_CAPACITY,
    SELLER_MEETING_CAPACITY,
    SETTER_MEETING_CAPACITY,
    BASE_DEALS_FROM_REVENUE,
    BASE_MEETINGS_REQUIRED,
    EST_MONTHLY_QUALIFIED_LEADS,
    BASE_QUALIFIED_LEADS_USED,
    BASE_MEETINGS_DEMAND,
    BASE_MEETINGS_REALIZED,
    BASE_DEALS_CLOSED,
    BASE_REVENUE_MODELED,
    BASE_REVENUE,
    AI_OUTBOUND_BOOKED_MEETINGS,
    AI_LEAD_TO_MEETING_RATE,
    AI_MEETING_TO_CLOSE_RATE,
    AI_SALES_CYCLE_DAYS,
    AI_MEETINGS_DEMAND_FROM_BASE_LEADS,
    AI_MEETINGS_DEMAND_TOTAL,
    AI_SELLER_MEETING_CAPACITY,
    AI_MEETINGS_REALIZED,
    AI_DEALS_CLOSED,
    AI_REVENUE_MODELED,
    MODELED_UPLIFT,
    AI_REVENUE,
    MONTHLY_REVENUE_LIFT,
    ANNUAL_REVENUE_LIFT,
    ANNUAL_AI_COST,
    NET_MONTHLY_BENEFIT,
    NET_ANNUAL_BENEFIT,
    PAYBACK_MONTHS,
    ROI_12_MONTHS,
  };
}

// ====================================================
// D) DEFAULT INPUTS
// ====================================================

const DEFAULT_INPUTS: CalculatorInputs = {
  MONTHLY_REVENUE: 500000,
  AVG_DEAL_SIZE: 5000,
  SALES_CYCLE_DAYS: 30,
  QUALIFIED_LEADS_KNOWN: true,
  MONTHLY_QUALIFIED_LEADS: 100,
  LEAD_TO_MEETING_RATE: 0.33,
  MEETING_TO_CLOSE_RATE: 0.25,
  NUM_SDR: 5,
  NUM_AE: 2,
  SDR_MAX_MEETINGS_PER_MONTH: 25,
  AE_MAX_MEETINGS_PER_MONTH: 40,
  FOUNDER_IS_SELLER: true,
  FOUNDER_MAX_MEETINGS_PER_MONTH: 20,
  AI_OUTBOUND_ENABLED: true,
  AI_OUTBOUND_LEADS_PER_MONTH: 400,
  AI_OUTBOUND_TO_MEETING_RATE: 0.017,
  AI_MONTHLY_COST: 5000,
  SCENARIO: "EXPECTED",
  D_TOO_MANY_UNQUALIFIED_LEADS: false,
  D_NOT_ENOUGH_LEADS: false,
  D_SLOW_RESPONSE_TIME: false,
  D_INCONSISTENT_FOLLOWUP: false,
  D_POOR_LEAD_ROUTING: false,
  D_MISS_DISCOVERY_QUESTIONS: false,
  D_INCONSISTENT_QUALIFICATION: false,
  D_DEALS_STALL_POST_DEMO: false,
  D_WEAK_PROPOSALS_NEXT_STEPS: false,
  D_FOUNDER_REQUIRED_FOR_DEALS: false,
  D_TRIBAL_KNOWLEDGE_LOCKED: false,
  D_LOW_TECHNICAL_DEPTH: false,
  D_TOOLS_NOT_INTEGRATED: false,
  D_MANUAL_CRM_UPDATES: false,
  D_LOW_PIPELINE_VISIBILITY: false,
};

// ====================================================
// E) UI COMPONENT
// ====================================================

export default function AIRevenueGrowthROICalculator() {
  const [inputs, setInputs] = useState<CalculatorInputs>(DEFAULT_INPUTS);
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    currentState: true,
    funnelMetrics: false,
    teamCapacity: false,
    founderSeller: false,
    aiOutbound: false,
    detractors: false,
    scenarioCost: false,
  });

  const results = useMemo(() => calculateResults(inputs), [inputs]);

  const updateInput = <K extends keyof CalculatorInputs>(
    key: K,
    value: CalculatorInputs[K]
  ) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return value.toFixed(2);
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          AI Revenue Growth ROI Calculator
        </h1>
        <p className="text-gray-600">
          Calculate the revenue impact of implementing AI-powered sales
          automation
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT COLUMN: INPUTS */}
        <div className="space-y-4">
          {/* Section 1: Current State */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection("currentState")}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                1. Current State
              </h2>
              <span className="text-gray-500">
                {expandedSections.currentState ? "▼" : "▶"}
              </span>
            </button>
            {expandedSections.currentState && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Revenue: {formatCurrency(inputs.MONTHLY_REVENUE)}
                    <span
                      className="ml-1 text-gray-400 cursor-help"
                      title="Your current monthly recurring revenue"
                    >
                      ⓘ
                    </span>
                  </label>
                  <input
                    type="range"
                    min="100000"
                    max="2000000"
                    step="10000"
                    value={inputs.MONTHLY_REVENUE}
                    onChange={(e) =>
                      updateInput("MONTHLY_REVENUE", parseFloat(e.target.value))
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$100k</span>
                    <span>$2M</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Average Deal Size: {formatCurrency(inputs.AVG_DEAL_SIZE)}
                    <span
                      className="ml-1 text-gray-400 cursor-help"
                      title="Average value of a closed deal"
                    >
                      ⓘ
                    </span>
                  </label>
                  <input
                    type="range"
                    min="2000"
                    max="100000"
                    step="1000"
                    value={inputs.AVG_DEAL_SIZE}
                    onChange={(e) =>
                      updateInput("AVG_DEAL_SIZE", parseFloat(e.target.value))
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$2k</span>
                    <span>$100k</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sales Cycle: {inputs.SALES_CYCLE_DAYS} days
                    <span
                      className="ml-1 text-gray-400 cursor-help"
                      title="Average days from first contact to close"
                    >
                      ⓘ
                    </span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="180"
                    step="1"
                    value={inputs.SALES_CYCLE_DAYS}
                    onChange={(e) =>
                      updateInput("SALES_CYCLE_DAYS", parseFloat(e.target.value))
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 day</span>
                    <span>180 days</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Funnel Metrics */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection("funnelMetrics")}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                2. Funnel Metrics
              </h2>
              <span className="text-gray-500">
                {expandedSections.funnelMetrics ? "▼" : "▶"}
              </span>
            </button>
            {expandedSections.funnelMetrics && (
              <div className="p-4 space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={inputs.QUALIFIED_LEADS_KNOWN}
                    onChange={(e) =>
                      updateInput("QUALIFIED_LEADS_KNOWN", e.target.checked)
                    }
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    I know my monthly qualified leads
                  </label>
                </div>
                {inputs.QUALIFIED_LEADS_KNOWN && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Qualified Leads: {inputs.MONTHLY_QUALIFIED_LEADS}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1000"
                      step="10"
                      value={inputs.MONTHLY_QUALIFIED_LEADS}
                      onChange={(e) =>
                        updateInput(
                          "MONTHLY_QUALIFIED_LEADS",
                          parseFloat(e.target.value)
                        )
                      }
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>0</span>
                      <span>1000</span>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lead to Meeting Rate: {Math.round(inputs.LEAD_TO_MEETING_RATE * 100)}%
                    <span
                      className="ml-1 text-gray-400 cursor-help"
                      title="Percentage of leads that convert to meetings"
                    >
                      ⓘ
                    </span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={Math.round(inputs.LEAD_TO_MEETING_RATE * 100)}
                    onChange={(e) =>
                      updateInput(
                        "LEAD_TO_MEETING_RATE",
                        parseFloat(e.target.value) / 100
                      )
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meeting to Close Rate: {Math.round(inputs.MEETING_TO_CLOSE_RATE * 100)}%
                    <span
                      className="ml-1 text-gray-400 cursor-help"
                      title="Percentage of meetings that convert to closed deals"
                    >
                      ⓘ
                    </span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={Math.round(inputs.MEETING_TO_CLOSE_RATE * 100)}
                    onChange={(e) =>
                      updateInput(
                        "MEETING_TO_CLOSE_RATE",
                        parseFloat(e.target.value) / 100
                      )
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Team & Capacity */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection("teamCapacity")}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                3. Team & Capacity
              </h2>
              <span className="text-gray-500">
                {expandedSections.teamCapacity ? "▼" : "▶"}
              </span>
            </button>
            {expandedSections.teamCapacity && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of SDRs: {inputs.NUM_SDR}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    value={inputs.NUM_SDR}
                    onChange={(e) =>
                      updateInput("NUM_SDR", parseInt(e.target.value))
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0</span>
                    <span>50</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of AEs: {inputs.NUM_AE}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="25"
                    step="1"
                    value={inputs.NUM_AE}
                    onChange={(e) =>
                      updateInput("NUM_AE", parseInt(e.target.value))
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0</span>
                    <span>25</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SDR Max Meetings/Month: {inputs.SDR_MAX_MEETINGS_PER_MONTH}
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="50"
                    step="1"
                    value={inputs.SDR_MAX_MEETINGS_PER_MONTH}
                    onChange={(e) =>
                      updateInput(
                        "SDR_MAX_MEETINGS_PER_MONTH",
                        parseFloat(e.target.value)
                      )
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5</span>
                    <span>50</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AE Max Meetings/Month: {inputs.AE_MAX_MEETINGS_PER_MONTH}
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="100"
                    step="1"
                    value={inputs.AE_MAX_MEETINGS_PER_MONTH}
                    onChange={(e) =>
                      updateInput(
                        "AE_MAX_MEETINGS_PER_MONTH",
                        parseFloat(e.target.value)
                      )
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5</span>
                    <span>100</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Founder Seller */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection("founderSeller")}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                4. Founder Seller
              </h2>
              <span className="text-gray-500">
                {expandedSections.founderSeller ? "▼" : "▶"}
              </span>
            </button>
            {expandedSections.founderSeller && (
              <div className="p-4 space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={inputs.FOUNDER_IS_SELLER}
                    onChange={(e) =>
                      updateInput("FOUNDER_IS_SELLER", e.target.checked)
                    }
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Founder is Seller
                  </label>
                </div>
                {inputs.FOUNDER_IS_SELLER && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Founder Max Meetings/Month: {inputs.FOUNDER_MAX_MEETINGS_PER_MONTH}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      step="1"
                      value={inputs.FOUNDER_MAX_MEETINGS_PER_MONTH}
                      onChange={(e) =>
                        updateInput(
                          "FOUNDER_MAX_MEETINGS_PER_MONTH",
                          parseFloat(e.target.value)
                        )
                      }
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>0</span>
                      <span>50</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 5: AI Outbound Engine */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection("aiOutbound")}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                5. AI Outbound Engine
              </h2>
              <span className="text-gray-500">
                {expandedSections.aiOutbound ? "▼" : "▶"}
              </span>
            </button>
            {expandedSections.aiOutbound && (
              <div className="p-4 space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={inputs.AI_OUTBOUND_ENABLED}
                    onChange={(e) =>
                      updateInput("AI_OUTBOUND_ENABLED", e.target.checked)
                    }
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Enable AI Outbound
                  </label>
                </div>
                {inputs.AI_OUTBOUND_ENABLED && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        AI Outbound Leads/Month
                      </label>
                      <input
                        type="number"
                        value={inputs.AI_OUTBOUND_LEADS_PER_MONTH || 0}
                        onChange={(e) =>
                          updateInput(
                            "AI_OUTBOUND_LEADS_PER_MONTH",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        AI Outbound to Meeting Rate: {(inputs.AI_OUTBOUND_TO_MEETING_RATE * 100).toFixed(1)}%
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        step="0.1"
                        value={inputs.AI_OUTBOUND_TO_MEETING_RATE * 100}
                        onChange={(e) =>
                          updateInput(
                            "AI_OUTBOUND_TO_MEETING_RATE",
                            parseFloat(e.target.value) / 100
                          )
                        }
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>1%</span>
                        <span>5%</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Section 6: Detractors */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection("detractors")}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                6. Sales Challenges (Detractors)
              </h2>
              <span className="text-gray-500">
                {expandedSections.detractors ? "▼" : "▶"}
              </span>
            </button>
            {expandedSections.detractors && (
              <div className="p-4 space-y-3">
                <p className="text-sm text-gray-600 mb-3">
                  Select all challenges that apply to your sales process:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_TOO_MANY_UNQUALIFIED_LEADS}
                      onChange={(e) =>
                        updateInput("D_TOO_MANY_UNQUALIFIED_LEADS", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Too many unqualified leads
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_NOT_ENOUGH_LEADS}
                      onChange={(e) =>
                        updateInput("D_NOT_ENOUGH_LEADS", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Not enough leads
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_SLOW_RESPONSE_TIME}
                      onChange={(e) =>
                        updateInput("D_SLOW_RESPONSE_TIME", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Slow response time
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_INCONSISTENT_FOLLOWUP}
                      onChange={(e) =>
                        updateInput("D_INCONSISTENT_FOLLOWUP", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Inconsistent follow-up
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_POOR_LEAD_ROUTING}
                      onChange={(e) =>
                        updateInput("D_POOR_LEAD_ROUTING", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Poor lead routing
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_MISS_DISCOVERY_QUESTIONS}
                      onChange={(e) =>
                        updateInput("D_MISS_DISCOVERY_QUESTIONS", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Missing discovery questions
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_INCONSISTENT_QUALIFICATION}
                      onChange={(e) =>
                        updateInput("D_INCONSISTENT_QUALIFICATION", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Inconsistent qualification
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_DEALS_STALL_POST_DEMO}
                      onChange={(e) =>
                        updateInput("D_DEALS_STALL_POST_DEMO", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Deals stall post-demo
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_WEAK_PROPOSALS_NEXT_STEPS}
                      onChange={(e) =>
                        updateInput("D_WEAK_PROPOSALS_NEXT_STEPS", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Weak proposals & next steps
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_FOUNDER_REQUIRED_FOR_DEALS}
                      onChange={(e) =>
                        updateInput("D_FOUNDER_REQUIRED_FOR_DEALS", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Founder required for deals
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_TRIBAL_KNOWLEDGE_LOCKED}
                      onChange={(e) =>
                        updateInput("D_TRIBAL_KNOWLEDGE_LOCKED", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Tribal knowledge locked
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_LOW_TECHNICAL_DEPTH}
                      onChange={(e) =>
                        updateInput("D_LOW_TECHNICAL_DEPTH", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Low technical depth
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_TOOLS_NOT_INTEGRATED}
                      onChange={(e) =>
                        updateInput("D_TOOLS_NOT_INTEGRATED", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Tools not integrated
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_MANUAL_CRM_UPDATES}
                      onChange={(e) =>
                        updateInput("D_MANUAL_CRM_UPDATES", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Manual CRM updates
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={inputs.D_LOW_PIPELINE_VISIBILITY}
                      onChange={(e) =>
                        updateInput("D_LOW_PIPELINE_VISIBILITY", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <label className="text-sm text-gray-700">
                      Low pipeline visibility
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 7: Scenario & AI Cost */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection("scenarioCost")}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
            >
              <h2 className="text-lg font-semibold text-gray-900">
                7. Scenario & AI Cost
              </h2>
              <span className="text-gray-500">
                {expandedSections.scenarioCost ? "▼" : "▶"}
              </span>
            </button>
            {expandedSections.scenarioCost && (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scenario
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => updateInput("SCENARIO", "CONSERVATIVE")}
                      className={`px-4 py-3 rounded-md font-medium transition-colors ${
                        inputs.SCENARIO === "CONSERVATIVE" ?"bg-blue-600 text-white" :"bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <div className="text-sm">Conservative</div>
                      <div className="text-xs mt-1">60%</div>
                    </button>
                    <button
                      onClick={() => updateInput("SCENARIO", "EXPECTED")}
                      className={`px-4 py-3 rounded-md font-medium transition-colors ${
                        inputs.SCENARIO === "EXPECTED" ?"bg-blue-600 text-white" :"bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <div className="text-sm">Expected</div>
                      <div className="text-xs mt-1">100%</div>
                    </button>
                    <button
                      onClick={() => updateInput("SCENARIO", "AGGRESSIVE")}
                      className={`px-4 py-3 rounded-md font-medium transition-colors ${
                        inputs.SCENARIO === "AGGRESSIVE" ?"bg-blue-600 text-white" :"bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <div className="text-sm">Aggressive</div>
                      <div className="text-xs mt-1">140%</div>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AI Monthly Cost ($)
                  </label>
                  <input
                    type="number"
                    value={inputs.AI_MONTHLY_COST || 0}
                    onChange={(e) =>
                      updateInput("AI_MONTHLY_COST", parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: RESULTS */}
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Revenue Impact
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Monthly Revenue Lift:</span>
                <span className="text-2xl font-bold text-green-600">
                  {formatCurrency(results.MONTHLY_REVENUE_LIFT)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Annual Revenue Lift:</span>
                <span className="text-xl font-bold text-green-600">
                  {formatCurrency(results.ANNUAL_REVENUE_LIFT)}
                </span>
              </div>
            </div>
          </div>

          {/* ROI Metrics */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              ROI Metrics (Revenue-Based)
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Net Monthly Benefit (Revenue):</span>
                <span className="font-semibold">
                  {formatCurrency(results.NET_MONTHLY_BENEFIT)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Net Annual Benefit (Revenue):</span>
                <span className="font-semibold">
                  {formatCurrency(results.NET_ANNUAL_BENEFIT)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">12-Month ROI (Revenue-Based):</span>
                <span className="font-semibold">
                  {typeof results.ROI_12_MONTHS === "number"
                    ? formatPercent(results.ROI_12_MONTHS)
                    : results.ROI_12_MONTHS}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Payback Period:</span>
                <span className="font-semibold">
                  {typeof results.PAYBACK_MONTHS === "number"
                    ? `${formatNumber(results.PAYBACK_MONTHS)} months`
                    : results.PAYBACK_MONTHS}
                </span>
              </div>
            </div>
          </div>

          {/* Funnel Improvements */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Funnel Improvements
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Lead to Meeting Lift:</span>
                <span className="font-semibold text-blue-600">
                  +{formatPercent(results.L2M_LIFT)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Meeting to Close Lift:</span>
                <span className="font-semibold text-blue-600">
                  +{formatPercent(results.CLOSE_LIFT)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Sales Cycle Reduction:</span>
                <span className="font-semibold text-blue-600">
                  -{formatPercent(results.CYCLE_REDUCTION)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Seller Capacity Lift:</span>
                <span className="font-semibold text-blue-600">
                  +{formatPercent(results.SELLER_CAPACITY_LIFT)}
                </span>
              </div>
            </div>
          </div>

          {/* Capacity Analysis */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Capacity Analysis
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Base Meetings Realized:</span>
                <span className="font-semibold">
                  {formatNumber(results.BASE_MEETINGS_REALIZED)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">AI Meetings Realized:</span>
                <span className="font-semibold text-green-600">
                  {formatNumber(results.AI_MEETINGS_REALIZED)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Base Deals Closed:</span>
                <span className="font-semibold">
                  {formatNumber(results.BASE_DEALS_CLOSED)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">AI Deals Closed:</span>
                <span className="font-semibold text-green-600">
                  {formatNumber(results.AI_DEALS_CLOSED)}
                </span>
              </div>
            </div>
          </div>

          {/* Calculation Details Toggle */}
          <div>
            <button
              onClick={() => setShowCalculationDetails(!showCalculationDetails)}
              className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-center font-medium text-gray-900"
            >
              {showCalculationDetails ? "Hide" : "Show"} Calculation Details
            </button>
          </div>

          {/* Calculation Details */}
          {showCalculationDetails && (
            <div className="bg-white p-6 rounded-lg border border-gray-200 overflow-auto max-h-96">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Calculation Details
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Variable</th>
                    <th className="text-right py-2">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(results).map(([key, value]) => (
                    <tr key={key} className="border-b">
                      <td className="py-2 text-gray-700">{key}</td>
                      <td className="py-2 text-right font-mono">
                        {typeof value === "number"
                          ? formatNumber(value)
                          : String(value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Assumptions */}
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              Assumptions
            </h2>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>• Revenue Blend = 0.7 (70% reported, 30% modeled)</li>
              <li>
                • Outbound meetings = leads × outbound-to-meeting rate
              </li>
              <li>
                • If no SDRs, setter capacity does not limit meetings
              </li>
              <li>
                • Founder capacity always counts when founder is seller
              </li>
              <li>• All ROI calculations are revenue-based only</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}